import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Landmark,
  Loader2,
  Receipt,
} from 'lucide-react';
import { AdminModulePage, PageHero } from '../components/PageTemplates';

type SubscriptionMode = 'self_serve' | 'contract_managed';
type BillingCollectionMethod = 'charge_automatically' | 'send_invoice';

interface StripeConfig {
  publishableKey: string | null;
  configured: boolean;
  leagueId: string;
  appBaseUrl: string;
  terms: {
    subscriptionMonthlyCents: number;
    platformFeeBps: number;
    platformFeeFlatCents: number;
    subscriptionMode?: SubscriptionMode;
    collectionMethod?: BillingCollectionMethod;
    updatedAt: string;
  };
}

interface BillingTerms {
  subscription_monthly_cents: number;
  platform_fee_bps: number;
  platform_fee_flat_cents: number;
  subscription_mode?: SubscriptionMode;
  collection_method?: BillingCollectionMethod;
  notes: string | null;
  updated_at: string;
}

interface SubscriptionState {
  stripeSubscriptionId: string;
  status: string;
  priceId: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  updatedAt: string;
}

interface ConnectState {
  stripeAccountId: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted?: boolean;
  updatedAt?: string;
}

interface StripeStatus {
  leagueId: string;
  customer: { stripeCustomerId: string | null };
  subscription: SubscriptionState | null;
  connect: ConnectState | null;
}

interface InvoiceRow {
  id: string;
  number: string | null;
  status: string | null;
  currency: string;
  amountDueCents: number;
  amountPaidCents: number;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  createdAt: string | null;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

interface PaymentRow {
  stripe_payment_intent_id: string;
  tournament_id: string | null;
  registration_id: string | null;
  stripe_account_id: string | null;
  amount_cents: number;
  application_fee_cents: number;
  currency: string;
  status: string;
  customer_email: string | null;
  created_at: string;
  updated_at: string;
}

interface PaymentSummary {
  currency: string;
  total_count: number;
  successful_count: number;
  pending_count: number;
  failed_count: number;
  refunded_count: number;
  gross_successful_cents: number;
  gross_refunded_cents: number;
  application_fee_cents: number;
  estimated_net_before_stripe_fees_cents: number;
}

const fallbackTerms = {
  subscriptionMonthlyCents: 5900,
  platformFeeBps: 290,
  platformFeeFlatCents: 50,
  subscriptionMode: 'self_serve' as SubscriptionMode,
  collectionMethod: 'charge_automatically' as BillingCollectionMethod,
  updatedAt: '',
};

const emptyPaymentSummary: PaymentSummary = {
  currency: 'usd',
  total_count: 0,
  successful_count: 0,
  pending_count: 0,
  failed_count: 0,
  refunded_count: 0,
  gross_successful_cents: 0,
  gross_refunded_cents: 0,
  application_fee_cents: 0,
  estimated_net_before_stripe_fees_cents: 0,
};

export function BillingPage() {
  const [config, setConfig] = useState<StripeConfig | null>(null);
  const [terms, setTerms] = useState<BillingTerms | null>(null);
  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary>(emptyPaymentSummary);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [cfgRes, statRes, invoiceRes, termsRes, paymentsRes] = await Promise.all([
        fetch('/api/stripe/config', { credentials: 'include' }),
        fetch('/api/stripe/status', { credentials: 'include' }),
        fetch('/api/stripe/invoices?limit=12', { credentials: 'include' }),
        fetch('/api/stripe/terms', { credentials: 'include' }),
        fetch('/api/stripe/payments?limit=12', { credentials: 'include' }),
      ]);

      if (cfgRes.ok) setConfig(await cfgRes.json());
      if (statRes.ok) setStatus(await statRes.json());
      else if (statRes.status === 401) setError('You need to be signed in as an admin to view Financials.');

      if (invoiceRes.ok) {
        const data = (await invoiceRes.json()) as { invoices: InvoiceRow[] };
        setInvoices(data.invoices ?? []);
      }

      if (termsRes.ok) {
        const data = (await termsRes.json()) as { terms: BillingTerms };
        setTerms(data.terms);
      }

      if (paymentsRes.ok) {
        const data = (await paymentsRes.json()) as { payments: PaymentRow[]; summary?: PaymentSummary };
        setPayments(data.payments ?? []);
        setPaymentSummary(data.summary ?? emptyPaymentSummary);
      }
    } catch (err) {
      setError(`Failed to load financial data: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      setBanner('Subscription checkout completed. Stripe may take a few seconds to update this page.');
    } else if (params.get('subscription') === 'canceled') {
      setBanner('Subscription checkout was canceled.');
    } else if (params.get('connect') === 'return') {
      setBanner('Returned from Stripe payouts setup. Status will refresh shortly.');
    } else if (params.get('connect') === 'refresh') {
      setBanner('Stripe setup link expired. Generate a new payout setup link below.');
    }
  }, []);

  const post = async (path: string, key: string): Promise<void> => {
    setActionLoading(key);
    setError(null);
    try {
      const res = await fetch(path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string; detail?: string };
      if (!res.ok) throw new Error(friendlyStripeError(data.error, data.detail, res.status));
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(null);
    }
  };

  const displayTerms = useMemo(() => ({
    monthlyCents: terms?.subscription_monthly_cents ?? config?.terms.subscriptionMonthlyCents ?? fallbackTerms.subscriptionMonthlyCents,
    platformFeeBps: terms?.platform_fee_bps ?? config?.terms.platformFeeBps ?? fallbackTerms.platformFeeBps,
    platformFeeFlatCents: terms?.platform_fee_flat_cents ?? config?.terms.platformFeeFlatCents ?? fallbackTerms.platformFeeFlatCents,
    subscriptionMode: terms?.subscription_mode ?? config?.terms.subscriptionMode ?? fallbackTerms.subscriptionMode,
    collectionMethod: terms?.collection_method ?? config?.terms.collectionMethod ?? fallbackTerms.collectionMethod,
    updatedAt: terms?.updated_at ?? config?.terms.updatedAt ?? fallbackTerms.updatedAt,
    notes: terms?.notes ?? null,
  }), [config?.terms, terms]);

  const subscriptionStatus = status?.subscription?.status ?? 'not subscribed';
  const subscriptionActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  const hasStripeCustomer = Boolean(status?.customer.stripeCustomerId);
  const connect = status?.connect ?? null;
  const payoutReady = Boolean(connect?.chargesEnabled && connect.payoutsEnabled);
  const contractManaged = displayTerms.subscriptionMode === 'contract_managed';
  const selfServeAutoBilling =
    displayTerms.subscriptionMode === 'self_serve' && displayTerms.collectionMethod === 'charge_automatically';

  return (
    <AdminModulePage>
      <PageHero
        title="Financials"
        description="Track what your league pays The Scorekeeper, what registrations collect, and where Stripe payout money lands."
        icon={Receipt}
        accent="arc"
      />

      {banner && (
        <Notice tone="info" onDismiss={() => setBanner(null)}>
          {banner}
        </Notice>
      )}

      {error && (
        <Notice tone="warn" onDismiss={() => setError(null)}>
          {error}
        </Notice>
      )}

      {!loading && config && !config.configured && (
        <Notice tone="warn">
          Stripe is not configured on this deploy. Financial actions need Stripe environment variables in Cloudflare Pages.
        </Notice>
      )}

      <div className="settings-grid" style={{ marginTop: 16 }}>
        <section className="card settings-card">
          <div className="card__head">
            <div>
              <h3 className="card__title">Subscription to The Scorekeeper</h3>
              <p className="card__sub">The monthly platform subscription for this League HQ.</p>
            </div>
            <StatusChip status={subscriptionStatus} ok={subscriptionActive} />
          </div>
          <div className="settings-card__body stack">
            <MetricBlock
              label="Current monthly rate"
              value={money(displayTerms.monthlyCents)}
              suffix="/ month"
            />
            <div className="note">
              {subscriptionPeriodCopy(status?.subscription)}
              {status?.subscription?.cancelAtPeriodEnd ? ' Cancellation is scheduled for period end.' : ''}
            </div>
            {contractManaged && (
              <div className="note note--soft">
                Billing is managed by The Scorekeeper. Invoices and payment status will appear here.
              </div>
            )}
            {!contractManaged && displayTerms.collectionMethod === 'send_invoice' && (
              <div className="note note--soft">
                This account is configured for invoice billing. The Scorekeeper will issue invoices through Stripe.
              </div>
            )}
            <div className="row" style={{ gap: 8 }}>
              {!subscriptionActive && selfServeAutoBilling && (
                <LoadingButton
                  loading={actionLoading === 'checkout'}
                  disabled={!config?.configured || actionLoading !== null}
                  onClick={() => post('/api/stripe/subscription/checkout', 'checkout')}
                  className="btn--primary"
                >
                  <CreditCard size={13} />
                  {actionLoading === 'checkout' ? 'Opening...' : 'Activate subscription'}
                </LoadingButton>
              )}
              {hasStripeCustomer && (
                <LoadingButton
                  loading={actionLoading === 'portal'}
                  disabled={!config?.configured || actionLoading !== null}
                  onClick={() => post('/api/stripe/subscription/portal', 'portal')}
                  className="btn--ghost"
                >
                  <ExternalLink size={13} />
                  {actionLoading === 'portal' ? 'Opening...' : 'Open billing portal'}
                </LoadingButton>
              )}
            </div>
          </div>
        </section>

        <section className="card settings-card">
          <div className="card__head">
            <div>
              <h3 className="card__title">Registration Revenue</h3>
              <p className="card__sub">Stripe registration payments mirrored from webhooks.</p>
            </div>
            <StatusChip status={`${paymentSummary.successful_count} paid`} ok={paymentSummary.successful_count > 0} />
          </div>
          <div className="settings-card__body stack">
            <div className="financials-metric-grid">
              <MetricBlock label="Gross collected" value={money(paymentSummary.gross_successful_cents, paymentSummary.currency)} />
              <MetricBlock label="Estimated net" value={money(paymentSummary.estimated_net_before_stripe_fees_cents, paymentSummary.currency)} />
              <MetricBlock label="Platform fees" value={money(paymentSummary.application_fee_cents, paymentSummary.currency)} />
              <MetricBlock label="Successful payments" value={String(paymentSummary.successful_count)} />
            </div>
            <div className="note note--soft">
              Estimated net subtracts Scorekeeper platform fees only. Stripe processing fees, exact bank deposits,
              refunds, disputes, and payout timing stay in Stripe Express for v1.
            </div>
          </div>
        </section>

        <section className="card settings-card">
          <div className="card__head">
            <div>
              <h3 className="card__title">Payout Account</h3>
              <p className="card__sub">Stripe Express account used for registration payment deposits.</p>
            </div>
            <StatusChip
              status={!connect ? 'not connected' : payoutReady ? 'ready' : 'needs setup'}
              ok={payoutReady}
            />
          </div>
          <div className="settings-card__body stack">
            <div className="row" style={{ gap: 8 }}>
              <span className={`chip chip--${connect?.chargesEnabled ? 'ok' : 'warn'}`}>Charges {connect?.chargesEnabled ? 'enabled' : 'disabled'}</span>
              <span className={`chip chip--${connect?.payoutsEnabled ? 'ok' : 'warn'}`}>Payouts {connect?.payoutsEnabled ? 'enabled' : 'disabled'}</span>
            </div>
            {connect?.stripeAccountId && (
              <div className="muted" style={{ fontSize: 12 }}>
                Account <code>{connect.stripeAccountId}</code>
              </div>
            )}
            <div className="row" style={{ gap: 8 }}>
              {!payoutReady ? (
                <LoadingButton
                  loading={actionLoading === 'onboard'}
                  disabled={!config?.configured || actionLoading !== null}
                  onClick={() => post('/api/stripe/connect/onboard', 'onboard')}
                  className="btn--primary"
                >
                  <Landmark size={13} />
                  {actionLoading === 'onboard' ? 'Opening...' : connect ? 'Continue payout setup' : 'Set up payouts'}
                </LoadingButton>
              ) : (
                <LoadingButton
                  loading={actionLoading === 'connect-dashboard'}
                  disabled={!config?.configured || actionLoading !== null}
                  onClick={() => post('/api/stripe/connect/dashboard', 'connect-dashboard')}
                  className="btn--ghost"
                >
                  <ExternalLink size={13} />
                  {actionLoading === 'connect-dashboard' ? 'Opening...' : 'Open Stripe Express'}
                </LoadingButton>
              )}
            </div>
          </div>
        </section>
      </div>

      <section className="card" style={{ marginTop: 16 }}>
        <div className="card__head">
          <div>
            <h3 className="card__title">Recent Registration Payments</h3>
            <p className="card__sub">Stripe-only registration revenue for this league.</p>
          </div>
          <PaymentCountSummary summary={paymentSummary} />
        </div>
        {loading ? (
          <div className="empty">Loading registration payments...</div>
        ) : payments.length === 0 ? (
          <div className="empty">No Stripe registration payments yet.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Gross</th>
                <th>Platform fee</th>
                <th>Est. net</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.stripe_payment_intent_id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{dateShort(payment.created_at)}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{payment.registration_id ?? payment.stripe_payment_intent_id}</div>
                  </td>
                  <td className="muted">{payment.customer_email ?? '-'}</td>
                  <td><StatusChip status={payment.status} ok={payment.status === 'succeeded'} /></td>
                  <td style={{ fontWeight: 700 }}>{money(payment.amount_cents, payment.currency)}</td>
                  <td className="muted">{money(payment.application_fee_cents, payment.currency)}</td>
                  <td className="muted">{payment.status === 'succeeded' ? money(payment.amount_cents - payment.application_fee_cents, payment.currency) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card" style={{ marginTop: 16 }}>
        <div className="card__head">
          <div>
            <h3 className="card__title">Invoices & Receipts</h3>
            <p className="card__sub">The Scorekeeper subscription invoices and receipts from Stripe.</p>
          </div>
          {hasStripeCustomer && (
            <LoadingButton
              loading={actionLoading === 'invoice-portal'}
              disabled={!config?.configured || actionLoading !== null}
              onClick={() => post('/api/stripe/subscription/portal', 'invoice-portal')}
              className="btn--ghost"
            >
              <ExternalLink size={13} />
              {actionLoading === 'invoice-portal' ? 'Opening...' : 'Manage in Stripe'}
            </LoadingButton>
          )}
        </div>
        {loading ? (
          <div className="empty">Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div className="empty">No subscription invoices yet.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Billing Period</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{invoice.number ?? invoice.id}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{invoice.createdAt ? dateShort(invoice.createdAt) : '-'}</div>
                  </td>
                  <td className="muted">{periodLabel(invoice)}</td>
                  <td><StatusChip status={invoice.status ?? 'unknown'} ok={invoice.status === 'paid'} /></td>
                  <td style={{ fontWeight: 700 }}>{money(invoice.amountDueCents, invoice.currency)}</td>
                  <td className="muted">{invoice.paidAt ? dateShort(invoice.paidAt) : '-'}</td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      {invoice.hostedInvoiceUrl && (
                        <a className="btn btn--ghost btn--sm" href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                          View
                        </a>
                      )}
                      {invoice.invoicePdf && (
                        <a className="btn btn--ghost btn--sm" href={invoice.invoicePdf} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card" style={{ marginTop: 16 }}>
        <div className="card__head">
          <div>
            <h3 className="card__title">Negotiated Terms</h3>
            <p className="card__sub">Current commercial terms for this league.</p>
          </div>
        </div>
        <table className="table">
          <tbody>
            <tr>
              <td className="muted">Monthly platform fee</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(displayTerms.monthlyCents)}/mo</td>
            </tr>
            <tr>
              <td className="muted">Per-registration platform fee</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>
                {percent(displayTerms.platformFeeBps)} + {money(displayTerms.platformFeeFlatCents)}
              </td>
            </tr>
            <tr>
              <td className="muted">Billing mode</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{subscriptionModeLabel(displayTerms.subscriptionMode)}</td>
            </tr>
            <tr>
              <td className="muted">Collection method</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{collectionMethodLabel(displayTerms.collectionMethod)}</td>
            </tr>
            <tr>
              <td className="muted">Stripe processing</td>
              <td style={{ textAlign: 'right' }} className="muted">Shown in Stripe receipts and Express reporting</td>
            </tr>
            <tr>
              <td className="muted">Terms last updated</td>
              <td style={{ textAlign: 'right' }} className="muted">{displayTerms.updatedAt ? dateShort(displayTerms.updatedAt) : '-'}</td>
            </tr>
            {displayTerms.notes && (
              <tr>
                <td className="muted">Notes</td>
                <td style={{ textAlign: 'right' }}>{displayTerms.notes}</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </AdminModulePage>
  );
}

function Notice({ tone, children, onDismiss }: { tone: 'info' | 'warn'; children: ReactNode; onDismiss?: () => void }) {
  const Icon = tone === 'warn' ? AlertTriangle : CheckCircle2;
  const color = tone === 'warn' ? 'var(--warn)' : 'var(--info)';
  return (
    <div className="card" style={{ marginTop: 12, borderColor: color }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <Icon size={16} color={color} />
        <span>{children}</span>
        {onDismiss && (
          <button className="btn btn--ghost btn--sm" onClick={onDismiss} style={{ marginLeft: 'auto' }}>
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

function LoadingButton({
  loading,
  disabled,
  className,
  children,
  onClick,
}: {
  loading: boolean;
  disabled?: boolean;
  className: string;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`btn ${className} btn--sm ${loading ? 'is-loading' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-busy={loading}
    >
      {loading && <Loader2 size={13} className="spin" />}
      {children}
    </button>
  );
}

function MetricBlock({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1 }}>
        {value}
        {suffix && <span className="muted" style={{ fontSize: 13, fontWeight: 600, marginLeft: 6 }}>{suffix}</span>}
      </div>
    </div>
  );
}

function PaymentCountSummary({ summary }: { summary: PaymentSummary }) {
  return (
    <div className="row" style={{ gap: 8 }}>
      <span className="chip chip--ok">{summary.successful_count} paid</span>
      <span className="chip chip--info">{summary.pending_count} pending</span>
      <span className={`chip chip--${summary.failed_count > 0 ? 'warn' : 'info'}`}>{summary.failed_count} failed</span>
      <span className={`chip chip--${summary.refunded_count > 0 ? 'warn' : 'info'}`}>{summary.refunded_count} refunded</span>
    </div>
  );
}

function StatusChip({ status, ok }: { status: string; ok?: boolean }) {
  const tone = ok ? 'ok' : ['past_due', 'unpaid', 'incomplete', 'needs setup', 'not connected', 'not subscribed', 'failed', 'refunded'].includes(status) ? 'warn' : 'info';
  return <span className={`chip chip--${tone}`}>{status}</span>;
}

function money(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function percent(bps: number): string {
  return `${(bps / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

function dateShort(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function dateLong(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function periodLabel(invoice: InvoiceRow): string {
  if (!invoice.periodStart && !invoice.periodEnd) return '-';
  if (!invoice.periodStart) return `through ${dateShort(invoice.periodEnd!)}`;
  if (!invoice.periodEnd) return `from ${dateShort(invoice.periodStart)}`;
  return `${dateShort(invoice.periodStart)} - ${dateShort(invoice.periodEnd)}`;
}

function subscriptionPeriodCopy(subscription?: SubscriptionState | null): string {
  if (!subscription?.currentPeriodEnd) return 'No active billing period is on file yet.';
  return `Current period ends ${dateLong(subscription.currentPeriodEnd)}.`;
}

function subscriptionModeLabel(value: SubscriptionMode): string {
  return value === 'contract_managed' ? 'Contract managed' : 'Self-serve';
}

function collectionMethodLabel(value: BillingCollectionMethod): string {
  return value === 'send_invoice' ? 'Stripe invoice' : 'Automatic card/bank charge';
}

function friendlyStripeError(error?: string, detail?: string, status?: number): string {
  if (error === 'contract_managed') {
    return 'This league is contract-managed. The Scorekeeper will set up billing and invoices directly.';
  }
  if (error === 'invoice_managed') {
    return 'This league is configured for invoice billing. The Scorekeeper will issue invoices through Stripe.';
  }
  return detail ?? error ?? `Request failed (${status})`;
}
